import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
  Heading,
  Square,
  Icon,
} from '@chakra-ui/react';
import { FiDownload, FiTrash2 } from 'react-icons/fi';
import { FaDollarSign, FaShoppingCart } from 'react-icons/fa';
import jsPDF from 'jspdf';
import { SidebarWithHeader } from '../components';
import { formatPrice } from '../utils/helpers';

const STORAGE_KEY = 'external_orders';

const initialFormState = {
  clientName: '',
  phoneNumber: '',
  gouvernorat: '',
  address: '',
  itemName: '',
  quantity: '1',
  unitPrice: '0',
  notes: '',
};

function ExternalOrdersPage() {
  const toast = useToast();
  const [formData, setFormData] = useState(initialFormState);
  const [externalOrders, setExternalOrders] = useState([]);

  useEffect(() => {
    const savedOrders = localStorage.getItem(STORAGE_KEY);
    if (!savedOrders) {
      return;
    }

    try {
      const parsedOrders = JSON.parse(savedOrders);
      if (Array.isArray(parsedOrders)) {
        setExternalOrders(parsedOrders);
      }
    } catch (error) {
      setExternalOrders([]);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(externalOrders));
  }, [externalOrders]);

  const computedTotal = useMemo(() => {
    const quantity = Number(formData.quantity) || 0;
    const unitPrice = Number(formData.unitPrice) || 0;
    return quantity * unitPrice;
  }, [formData.quantity, formData.unitPrice]);

  const totalRevenue = useMemo(() => {
    return externalOrders.reduce(
      (accumulator, order) => accumulator + (Number(order.totalPrice) || 0),
      0
    );
  }, [externalOrders]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.clientName.trim()) {
      return 'Le nom du client est obligatoire';
    }
    if (!formData.phoneNumber.trim()) {
      return 'Le téléphone est obligatoire';
    }
    if (!formData.gouvernorat.trim()) {
      return 'Le gouvernorat est obligatoire';
    }
    if (!formData.address.trim()) {
      return 'L\'adresse est obligatoire';
    }
    if (!formData.itemName.trim()) {
      return 'La désignation est obligatoire';
    }
    if ((Number(formData.quantity) || 0) <= 0) {
      return 'La quantité doit être supérieure à 0';
    }
    if ((Number(formData.unitPrice) || 0) <= 0) {
      return 'Le prix unitaire doit être supérieur à 0';
    }
    return '';
  };

  const generateFacturePDF = (externalOrder) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(18);
    doc.setFontStyle('bold');
    doc.text('BON DE LIVRAISON', pageWidth / 2, 20, 'center');

    doc.setFontSize(10);
    doc.setFontStyle('normal');
    const orderDate = externalOrder.createdAt
      ? new Date(externalOrder.createdAt).toLocaleDateString('fr-TN')
      : new Date().toLocaleDateString('fr-TN');

    doc.setDrawColor(0);
    doc.rect(130, 30, 70, 25);
    doc.text('N: ' + externalOrder.id.slice(-8).toUpperCase(), 135, 38);
    doc.text('Date: ' + orderDate, 135, 45);
    doc.text('C.R: ' + Number(externalOrder.totalPrice).toFixed(2) + ' DT', 135, 52);

    doc.setFontStyle('bold');
    doc.text('Coordonnees Expediteur:', 15, 40);
    doc.setFontStyle('normal');
    doc.text('Compte: Art & Bois', 15, 47);
    doc.text('Adresse: Tunisie', 15, 54);
    doc.text('Tel: 54 457 134', 15, 61);

    doc.line(15, 68, pageWidth - 15, 68);

    doc.setFontStyle('bold');
    doc.text('Coordonnees Destinataire:', 15, 78);
    doc.setFontStyle('normal');
    doc.text('Nom: ' + externalOrder.clientName, 15, 85);
    doc.text('Gouvernorat: ' + externalOrder.gouvernorat, 15, 92);
    doc.text('Adresse: ' + externalOrder.address, 15, 99);
    doc.text('Tel: ' + externalOrder.phoneNumber, 15, 106);

    const tableTop = 120;
    doc.setFillColor(240, 240, 240);
    doc.rect(15, tableTop, pageWidth - 30, 10, 'F');
    doc.setFontStyle('bold');
    doc.text('Designation', 20, tableTop + 7);
    doc.text('P.U', 120, tableTop + 7);
    doc.text('Qte', 145, tableTop + 7);
    doc.text('Total', 170, tableTop + 7);

    doc.setFontStyle('normal');
    const itemPrice = Number(externalOrder.unitPrice) || 0;
    const itemQty = Number(externalOrder.quantity) || 1;
    const itemTotal = Number(externalOrder.totalPrice) || 0;
    const displayName =
      externalOrder.itemName.length > 35
        ? externalOrder.itemName.substring(0, 32) + '...'
        : externalOrder.itemName;

    let yPos = tableTop + 17;
    doc.text(displayName, 20, yPos);
    doc.text(itemPrice.toFixed(2), 120, yPos);
    doc.text(String(itemQty), 145, yPos);
    doc.text(itemTotal.toFixed(2), 170, yPos);

    doc.line(15, yPos + 5, pageWidth - 15, yPos + 5);

    const totalTTC = itemTotal;
    const totalHT = (totalTTC / 1.19).toFixed(2);
    const tva = (totalTTC - Number(totalHT)).toFixed(2);

    yPos += 15;
    doc.setFontStyle('bold');
    doc.text('Total HT:', 130, yPos);
    doc.text(totalHT + ' DT', 170, yPos);

    yPos += 8;
    doc.text('TVA (19%):', 130, yPos);
    doc.text(tva + ' DT', 170, yPos);

    yPos += 8;
    doc.setFontSize(12);
    doc.text('Total TTC:', 130, yPos);
    doc.text(totalTTC.toFixed(2) + ' DT', 170, yPos);

    if (externalOrder.notes) {
      doc.setFontSize(9);
      doc.setFontStyle('normal');
      doc.text('Notes: ' + externalOrder.notes, 15, yPos + 18);
    }

    doc.setFontSize(8);
    doc.setFontStyle('italic');
    doc.text('Merci pour votre confiance - Art & Bois', pageWidth / 2, 280, 'center');

    const fileName = `${externalOrder.clientName.replace(/\s+/g, '_')}_externe.pdf`;
    doc.save(fileName);

    toast({
      position: 'top',
      description: `Facture téléchargée: ${fileName}`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleCreateExternalOrder = (event) => {
    event.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      toast({
        position: 'top',
        description: validationError,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const newOrder = {
      id: `${Date.now()}`,
      clientName: formData.clientName.trim(),
      phoneNumber: formData.phoneNumber.trim(),
      gouvernorat: formData.gouvernorat.trim(),
      address: formData.address.trim(),
      itemName: formData.itemName.trim(),
      quantity: Number(formData.quantity),
      unitPrice: Number(formData.unitPrice),
      totalPrice: computedTotal,
      notes: formData.notes.trim(),
      createdAt: new Date().toISOString(),
      source: 'outside-platform',
    };

    setExternalOrders((previous) => [newOrder, ...previous]);
    setFormData(initialFormState);

    toast({
      position: 'top',
      description: 'Commande externe ajoutée avec succès',
      status: 'success',
      duration: 4000,
      isClosable: true,
    });
  };

  const handleDeleteOrder = (id) => {
    setExternalOrders((previous) => previous.filter((order) => order.id !== id));
    toast({
      position: 'top',
      description: 'Commande externe supprimée',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-TN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <SidebarWithHeader>
      <VStack spacing={5} align='stretch'>
        <SimpleGrid minChildWidth='250px' spacing={5}>
          <Flex shadow='lg' bg='white' p='5' borderRadius='lg' justifyContent='center'>
            <Box>
              <Text fontSize='1xl' color='gray.500'>
                Commandes Externes
              </Text>
              <Heading size='lg' color='brown.500'>
                {externalOrders.length}
              </Heading>
            </Box>
            <Box ml='auto' alignSelf='center'>
              <Square size='60px' bg='red.500' borderRadius='lg'>
                <Icon as={FaShoppingCart} color='white' />
              </Square>
            </Box>
          </Flex>

          <Flex shadow='lg' bg='white' p='5' borderRadius='lg' justifyContent='center'>
            <Box>
              <Text fontSize='1xl' color='gray.500'>
                Revenu Externe Total
              </Text>
              <Heading size='lg' color='green.500'>
                {formatPrice(totalRevenue)}
              </Heading>
            </Box>
            <Box ml='auto' alignSelf='center'>
              <Square size='60px' bg='red.500' borderRadius='lg'>
                <Icon as={FaDollarSign} color='white' />
              </Square>
            </Box>
          </Flex>
        </SimpleGrid>

        <Box bg='white' p={5} shadow='lg' borderRadius='lg'>
          <Heading size='md' mb={4}>
            Nouvelle commande externe
          </Heading>
          <form onSubmit={handleCreateExternalOrder}>
            <Grid templateColumns='repeat(12, 1fr)' gap={4}>
              <GridItem colSpan={{ base: 12, md: 6 }}>
                <FormControl isRequired>
                  <FormLabel>Nom client</FormLabel>
                  <Input
                    name='clientName'
                    value={formData.clientName}
                    onChange={handleInputChange}
                    placeholder='Ex: Mohamed Ben Ali'
                  />
                </FormControl>
              </GridItem>

              <GridItem colSpan={{ base: 12, md: 6 }}>
                <FormControl isRequired>
                  <FormLabel>Téléphone</FormLabel>
                  <Input
                    name='phoneNumber'
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder='Ex: 20 123 456'
                  />
                </FormControl>
              </GridItem>

              <GridItem colSpan={{ base: 12, md: 6 }}>
                <FormControl isRequired>
                  <FormLabel>Gouvernorat</FormLabel>
                  <Input
                    name='gouvernorat'
                    value={formData.gouvernorat}
                    onChange={handleInputChange}
                    placeholder='Ex: Ariana'
                  />
                </FormControl>
              </GridItem>

              <GridItem colSpan={{ base: 12, md: 6 }}>
                <FormControl isRequired>
                  <FormLabel>Adresse</FormLabel>
                  <Input
                    name='address'
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder='Adresse complète'
                  />
                </FormControl>
              </GridItem>

              <GridItem colSpan={{ base: 12, md: 6 }}>
                <FormControl isRequired>
                  <FormLabel>Désignation</FormLabel>
                  <Input
                    name='itemName'
                    value={formData.itemName}
                    onChange={handleInputChange}
                    placeholder='Produit / Service'
                  />
                </FormControl>
              </GridItem>

              <GridItem colSpan={{ base: 12, md: 3 }}>
                <FormControl isRequired>
                  <FormLabel>Quantité</FormLabel>
                  <NumberInput min={1} value={formData.quantity}>
                    <NumberInputField
                      name='quantity'
                      onChange={handleInputChange}
                    />
                  </NumberInput>
                </FormControl>
              </GridItem>

              <GridItem colSpan={{ base: 12, md: 3 }}>
                <FormControl isRequired>
                  <FormLabel>Prix Unitaire (DT)</FormLabel>
                  <NumberInput min={0} precision={2} value={formData.unitPrice}>
                    <NumberInputField
                      name='unitPrice'
                      onChange={handleInputChange}
                    />
                  </NumberInput>
                </FormControl>
              </GridItem>

              <GridItem colSpan={12}>
                <FormControl>
                  <FormLabel>Notes</FormLabel>
                  <Textarea
                    name='notes'
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder='Notes supplémentaires (optionnel)'
                  />
                </FormControl>
              </GridItem>
            </Grid>

            <HStack mt={4} justifyContent='space-between' flexWrap='wrap'>
              <Text fontWeight='bold'>
                Total calculé: {formatPrice(computedTotal)}
              </Text>
              <Button colorScheme='brown' type='submit'>
                Ajouter et enregistrer
              </Button>
            </HStack>
          </form>
        </Box>

        <Box bg='white' p={5} shadow='lg' borderRadius='lg' overflowX='auto'>
          <Heading size='md' mb={4}>
            Liste des clients externes
          </Heading>
          <Table variant='simple'>
            <Thead>
              <Tr>
                <Th>Client</Th>
                <Th>Téléphone</Th>
                <Th>Adresse</Th>
                <Th>Montant</Th>
                <Th>Date</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {externalOrders.map((order) => (
                <Tr key={order.id}>
                  <Td>{order.clientName}</Td>
                  <Td>{order.phoneNumber}</Td>
                  <Td>{order.address}</Td>
                  <Td>{formatPrice(order.totalPrice)}</Td>
                  <Td>{formatDate(order.createdAt)}</Td>
                  <Td>
                    <HStack>
                      <Button
                        leftIcon={<FiDownload />}
                        colorScheme='green'
                        size='sm'
                        onClick={() => generateFacturePDF(order)}
                      >
                        Télécharger facture
                      </Button>
                      <Button
                        leftIcon={<FiTrash2 />}
                        colorScheme='red'
                        size='sm'
                        variant='outline'
                        onClick={() => handleDeleteOrder(order.id)}
                      >
                        Supprimer
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </VStack>
    </SidebarWithHeader>
  );
}

export default ExternalOrdersPage;